import { useRef } from 'react'
import type { VatMode } from '@crm/shared'
import { useQueryClient } from '@tanstack/react-query'
import { createQuotation } from '../../../shared/api/quotations.api'
import type { MarkupConstants } from '../../../shared/api/exchangeRates.api'
import type { Request } from '../../../shared/api/types'
import { toast } from '../../../shared/ui/toastStore'
import { useAuthStore } from '../../auth/authStore'
import { buildKpItems } from '../utils/buildKpItems'
import { downloadKp, type KpFormat } from '../utils/downloadKp'
import { kpVatView } from '../utils/kpVat'
import type { SelectedPrice } from './useKpMode'

type Params = {
  request: Request
  selectedPrices: Map<string, SelectedPrice>
  kpMarkups: Record<string, number>
  positionOverrides: Record<string, number>
  exchangeRates: { USD: number; EUR: number; CNY: number }
  markupConstants: MarkupConstants
  /** Примечания к позициям из предпросмотра — идут только в файл, не в снапшот. */
  notes: Record<string, string>
  /** Режим НДС КП: «С НДС» или «НДС сверху». */
  vatMode: VatMode
  /** Файл скачан — выходим из режима КП. */
  onDone: () => void
}

/**
 * Скачивание КП из режима КП: сохранить снапшот на сервере, затем собрать файл.
 *
 * Это два разных шага, и падать они могут по отдельности (№308). Если снапшот
 * сохранился, а файл не собрался (ошибка генератора, не догрузился чанк PDF),
 * повторное нажатие формата НЕ создаёт второй снапшот: хук помнит «отпечаток»
 * сохранённого тела КП и при совпадении повторяет только генерацию файла —
 * через тот же downloadKp, что и скачивание сохранённого КП из истории. Раньше
 * каждая попытка плодила новую версию КП (и завышала «Сумму КП» в планировании).
 * Поменялись цены, наценки или курсы — отпечаток другой, это уже новое КП.
 *
 * Ошибка генерации файла пробрасывается наружу: тост показывает
 * KpDownloadModal, модалка остаётся открытой для повтора.
 */
export function useKpDownload({
  request,
  selectedPrices,
  kpMarkups,
  positionOverrides,
  exchangeRates,
  markupConstants,
  notes,
  vatMode,
  onDone,
}: Params) {
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  // Отпечаток тела последнего снапшота, который сервер точно сохранил.
  const savedSignatureRef = useRef<string | null>(null)

  const handleDownloadKp = async (format: KpFormat, entityId: string, recipient: string) => {
    const kpItems = buildKpItems({
      items: request.items ?? [],
      selectedPrices,
      kpMarkups,
      positionOverrides,
      vatMode,
    })
    if (kpItems.length === 0) {
      toast.error('Выберите хотя бы одну цену')
      return
    }
    // Сумма КП: «С НДС» — Σ цена × количество, «НДС сверху» — «Всего к оплате» как в 1С:УНФ.
    const totalSum = kpVatView(kpItems, vatMode).total
    // Снапшот замораживает курсы и наценки. Бэкенд в этой же транзакции
    // переводит заявку в QUOTATION_CREATED и делает каскад позиций —
    // дополнительные PATCH статуса не нужны.
    const body = {
      requestNumber: request.requestNumber ?? null,
      exchangeRates: {
        USD: exchangeRates.USD,
        EUR: exchangeRates.EUR,
        CNY: exchangeRates.CNY,
      },
      markupConstants: { ...markupConstants },
      markups: kpMarkups,
      positionOverrides,
      items: kpItems,
      totalSum: Math.round(totalSum * 100) / 100,
      vatMode,
    }
    const signature = JSON.stringify(body)

    if (savedSignatureRef.current !== signature) {
      try {
        await createQuotation(request.id, body)
        savedSignatureRef.current = signature
        // Обновляем всё что связано со статусом заявки и готовностью:
        //  — ['requests', id] чтобы RequestHeader подтянул status=QUOTATION_CREATED
        //  — ['readiness', id] для счётчиков в ActionsPanel
        //  — ['quotations', id] для списка снапшотов
        void queryClient.invalidateQueries({ queryKey: ['requests', request.id] })
        void queryClient.invalidateQueries({ queryKey: ['readiness', request.id] })
        void queryClient.invalidateQueries({ queryKey: ['quotations', request.id] })
      } catch (err) {
        // Русская причина от сервера лежит в response.data.error.message;
        // axios-овский err.message — «Request failed with status code 422».
        const msg =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message ?? (err instanceof Error ? err.message : 'Неизвестная ошибка')
        toast.error(`КП не сохранено на сервере: ${msg}\nСтатус заявки не изменится.`)
        // Продолжаем скачивание файла — чтобы менеджер не потерял данные
      }
    }

    // Скачиваем файл в выбранном формате. Ответственный = тот, кто выставляет
    // КП (вошедший пользователь), даже если это ADMIN/РОП в чужой заявке.
    await downloadKp(format, {
      entityId,
      recipient,
      requestNumber: request.requestNumber ?? undefined,
      clientName: request.client?.name ?? null,
      responsibleName: currentUser?.name ?? currentUser?.email ?? null,
      responsiblePhone: currentUser?.phone ?? null,
      responsibleEmail: currentUser?.personalEmail ?? null,
      vatMode,
      items: kpItems.map((it) => ({
        partNumber: it.partNumber,
        customerPartNumber: it.customerPartNumber,
        brand: it.brand,
        quantity: it.quantity,
        unit: it.unit,
        priceWithMarkup: it.priceWithMarkup,
        priceNet: it.priceNet,
        matrix: it.matrix,
        note: notes[it.itemId] ?? it.note ?? '',
      })),
    })

    // После скачивания КП автоматически выходим из режима — заявка уже в
    // QUOTATION_CREATED, повторно выставлять то же КП не нужно.
    onDone()
  }

  return { handleDownloadKp }
}
